import { useCallback, useEffect, useRef } from "react";
import { Box, Collapse, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LinkIcon from "@mui/icons-material/Link";
import type { DetailRouteContext, DetailRouteKind } from "../hooks/useDetailRoute";
import { supabase } from "../lib/supabase";
import { ActivityTimeline } from "../features/activities/ActivityTimeline";
import { DealTeamSection, DealTeamSummary } from "../features/deals/DealTeamSection";
import {
  useRelatedRailWidth,
  useRelatedRailCollapsed,
  useRelatedRailSections,
} from "../hooks/useRelatedRailWidth";
import { RelatedDealsList } from "./RelatedDealsList";
import { RelatedContactsList } from "./RelatedContactsList";
import { RelatedEnrichmentSlot } from "./RelatedEnrichmentSlot";
import { EntityFilesList } from "./EntityFilesList";
import { CommentThread } from "./CommentThread";

const COLLAPSED_W = 48;

type SectionKey =
  | "activity"
  | "deal_team"
  | "related_deals"
  | "related_contacts"
  | "files"
  | "comments"
  | "enrichment";

interface SectionDef {
  key: SectionKey;
  label: string;
  defaultExpanded: boolean;
  appliesTo: DetailRouteKind[];
}

const SECTIONS: SectionDef[] = [
  {
    key: "activity",
    label: "Activity",
    defaultExpanded: true,
    appliesTo: ["contact", "organization", "deal"],
  },
  {
    // Just below Activity on deals: Territory → AE/BDR (routing), Owner, Record.
    key: "deal_team",
    label: "Deal Team",
    defaultExpanded: true,
    appliesTo: ["deal"],
  },
  {
    key: "related_deals",
    label: "Related deals",
    defaultExpanded: true,
    appliesTo: ["contact", "organization", "partner"],
  },
  {
    key: "related_contacts",
    // Removed from deal pages (2026-06-16): on a deal this only showed the
    // single primary contact, already covered by the Roles table + the Primary
    // contact field. Still useful on org/partner where it aggregates contacts.
    label: "Related contacts",
    defaultExpanded: true,
    appliesTo: ["organization", "partner"],
  },
  {
    key: "files",
    label: "Files",
    defaultExpanded: false,
    appliesTo: ["contact", "organization", "deal", "partner"],
  },
  {
    key: "comments",
    label: "Comments",
    defaultExpanded: true,
    appliesTo: ["contact", "organization", "deal", "partner"],
  },
  {
    key: "enrichment",
    label: "Enrichment",
    defaultExpanded: false,
    appliesTo: ["contact", "organization"],
  },
];

const ACTIVITY_LINK_FIELD: Record<
  DetailRouteKind,
  "person_id" | "organization_id" | "deal_id" | null
> = {
  contact: "person_id",
  organization: "organization_id",
  deal: "deal_id",
  partner: null,
};

function Placeholder({ note }: { note: string }) {
  return (
    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", py: 1 }}>
      {note}
    </Typography>
  );
}

function SectionContent({ section, ctx }: { section: SectionKey; ctx: DetailRouteContext }) {
  if (section === "activity") {
    const linkField = ACTIVITY_LINK_FIELD[ctx.kind];
    if (!linkField) return <Placeholder note="Activity not tracked for this entity." />;
    return <ActivityTimeline linkField={linkField} linkId={ctx.id} />;
  }
  if (section === "deal_team") {
    if (ctx.kind !== "deal") return null;
    return <DealTeamSection dealId={ctx.id} />;
  }
  if (section === "related_deals") return <RelatedDealsList ctx={ctx} />;
  if (section === "related_contacts") return <RelatedContactsList ctx={ctx} />;
  if (section === "files")
    return (
      <EntityFilesList
        entityType={ctx.kind as "person" | "organization" | "deal"}
        entityId={ctx.id}
      />
    );
  if (section === "comments")
    return (
      <CommentThread
        entityType={ctx.kind as "person" | "organization" | "deal"}
        entityId={ctx.id}
      />
    );
  return <RelatedEnrichmentSlot ctx={ctx} />;
}

/** Count of rows for an entity in a view/table, only fetched when needed
 *  (i.e. the section is collapsed). Returns 0 until loaded. */
function useEntityCount(
  key: string,
  from: { schema?: "platform"; relation: string },
  ctx: DetailRouteContext,
  enabled: boolean,
) {
  return useQuery({
    queryKey: [key, "count", ctx.kind, ctx.id],
    enabled,
    staleTime: 30_000,
    queryFn: async () => {
      const base = from.schema ? supabase.schema(from.schema) : supabase;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { count, error } = await (base as any)
        .from(from.relation)
        .select("*", { count: "exact", head: true })
        .eq("entity_type", ctx.kind)
        .eq("entity_id", ctx.id);
      if (error) throw error;
      return (count as number | null) ?? 0;
    },
  });
}

/** Filled count bubble shown beside a collapsed section label. Renders nothing
 *  at zero — the point is to flag *hidden* content, not advertise emptiness. */
function CountBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <Box
      component="span"
      aria-label={`${count} hidden`}
      sx={{
        ml: 0.75,
        px: 0.5,
        minWidth: 18,
        height: 18,
        borderRadius: 999,
        bgcolor: "primary.main",
        color: "primary.contrastText",
        fontSize: 11,
        fontWeight: 600,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {count}
    </Box>
  );
}

/** Inline header extras for a collapsed section (next to the label). Mounts
 *  only when collapsed, so the count query doesn't run while expanded. */
function CollapsedBadge({ section, ctx }: { section: SectionKey; ctx: DetailRouteContext }) {
  const isComments = section === "comments";
  const isFiles = section === "files";
  const comments = useEntityCount(
    "comments",
    { relation: "manna_entity_comments_view" },
    ctx,
    isComments,
  );
  const files = useEntityCount(
    "attachments",
    { schema: "platform", relation: "entity_attachments" },
    ctx,
    isFiles,
  );
  if (isComments) return <CountBadge count={comments.data ?? 0} />;
  if (isFiles) return <CountBadge count={files.data ?? 0} />;
  return null;
}

/** Second-line summary for a collapsed section (below the label). */
function CollapsedSummaryLine({ section, ctx }: { section: SectionKey; ctx: DetailRouteContext }) {
  if (section === "deal_team" && ctx.kind === "deal") {
    return (
      <Box sx={{ px: 1.5, pb: 1, mt: -0.5 }}>
        <DealTeamSummary dealId={ctx.id} />
      </Box>
    );
  }
  return null;
}

function Section({
  def,
  ctx,
  expanded,
  onToggle,
}: {
  def: SectionDef;
  ctx: DetailRouteContext;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <Box sx={{ borderBottom: (t) => `1px solid ${t.palette.divider}` }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 1.5, py: 1, cursor: "pointer", userSelect: "none" }}
        onClick={onToggle}
      >
        <Stack direction="row" alignItems="center" sx={{ minWidth: 0 }}>
          <Typography
            variant="overline"
            sx={{ fontSize: 10.5, letterSpacing: 1.2, color: "text.secondary" }}
          >
            {def.label}
          </Typography>
          {!expanded && <CollapsedBadge section={def.key} ctx={ctx} />}
        </Stack>
        <IconButton
          size="small"
          aria-label={`Toggle ${def.label}`}
          aria-expanded={expanded}
          sx={{ p: 0.25 }}
        >
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Stack>
      {!expanded && <CollapsedSummaryLine section={def.key} ctx={ctx} />}
      <Collapse in={expanded} timeout="auto">
        <Box sx={{ px: 1.5, pb: 2 }}>
          <SectionContent section={def.key} ctx={ctx} />
        </Box>
      </Collapse>
    </Box>
  );
}

export function RelatedRail({ ctx }: { ctx: DetailRouteContext }) {
  const visible = SECTIONS.filter((s) => s.appliesTo.includes(ctx.kind));
  const { sections, setSectionExpanded } = useRelatedRailSections();

  // Server pref overrides per-section default; missing keys fall back to spec.
  const isExpanded = (s: SectionDef) =>
    typeof sections[s.key] === "boolean" ? (sections[s.key] as boolean) : s.defaultExpanded;

  const { widthPx, setWidthPx, isResizing, setIsResizing } = useRelatedRailWidth();
  const { collapsed, setCollapsed } = useRelatedRailCollapsed();
  const dragRef = useRef<{ startX: number; startW: number } | null>(null);
  const moveRef = useRef((_e: PointerEvent) => {});
  const upRef = useRef((_e: PointerEvent) => {});
  const stableMove = useRef((e: PointerEvent) => moveRef.current(e));
  const stableUp = useRef((e: PointerEvent) => upRef.current(e));

  useEffect(() => {
    moveRef.current = (e: PointerEvent) => {
      if (!dragRef.current) return;
      // Splitter on the left edge: dragging left → rail grows.
      const dx = dragRef.current.startX - e.clientX;
      setWidthPx(dragRef.current.startW + dx);
    };
    upRef.current = () => {
      dragRef.current = null;
      setIsResizing(false);
      window.removeEventListener("pointermove", stableMove.current);
      window.removeEventListener("pointerup", stableUp.current);
    };
  }, [setWidthPx, setIsResizing]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      dragRef.current = { startX: e.clientX, startW: widthPx };
      setIsResizing(true);
      window.addEventListener("pointermove", stableMove.current);
      window.addEventListener("pointerup", stableUp.current);
    },
    [widthPx, setIsResizing],
  );

  useEffect(
    () => () => {
      window.removeEventListener("pointermove", stableMove.current);
      window.removeEventListener("pointerup", stableUp.current);
    },
    [],
  );

  if (collapsed) {
    return (
      <Box
        component="aside"
        aria-label="Related rail"
        sx={{
          width: COLLAPSED_W,
          flexShrink: 0,
          borderLeft: (t) => `1px solid ${t.palette.divider}`,
          bgcolor: "background.default",
          height: "calc(100vh - 64px)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 1,
        }}
      >
        <Tooltip title="Expand Related" placement="left">
          <IconButton
            size="small"
            aria-label="Expand Related rail"
            onClick={() => setCollapsed(false)}
          >
            <ChevronLeftIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Expand Related" placement="left">
          <IconButton
            size="small"
            aria-label="Expand Related rail"
            onClick={() => setCollapsed(false)}
            sx={{ mt: 1 }}
          >
            <LinkIcon fontSize="small" sx={{ color: "text.secondary" }} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box
      component="aside"
      aria-label="Related rail"
      sx={{
        width: widthPx,
        flexShrink: 0,
        borderLeft: (t) => `1px solid ${t.palette.divider}`,
        bgcolor: "background.default",
        overflowY: "auto",
        height: "calc(100vh - 64px)",
        position: "relative",
        userSelect: isResizing ? "none" : undefined,
      }}
    >
      <Box
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize Related rail"
        onPointerDown={onPointerDown}
        sx={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: -2,
          width: 4,
          cursor: "col-resize",
          zIndex: 1,
          bgcolor: isResizing ? (t) => t.palette.primary.main : "transparent",
          "&:hover": { bgcolor: (t) => t.palette.divider },
        }}
      />
      <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ px: 1, pt: 0.5 }}>
        <Tooltip title="Collapse Related" placement="left">
          <IconButton
            size="small"
            aria-label="Collapse Related rail"
            onClick={() => setCollapsed(true)}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Stack divider={null}>
        {visible.map((s) => {
          const exp = isExpanded(s);
          return (
            <Section
              key={s.key}
              def={s}
              ctx={ctx}
              expanded={exp}
              onToggle={() => setSectionExpanded(s.key, !exp)}
            />
          );
        })}
      </Stack>
    </Box>
  );
}
